import { useContext, useState } from "react";
import { toast } from "sonner";
import { UserRoundPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { trpc } from "@/trpc";
import type { RouterOutputs } from "@/trpc";
import MerchantContext from "@/contexts/MerchantContext";
import UserContext from "@/contexts/UserContext";
import { canReassignLead } from "@/utils/permissions";
import posthog from "@/lib/posthog";
import EmployeePicker from "@/components/leads/chat/EmployeePicker";
import LeadAvatar from "@/components/leads/chat/LeadAvatar";

type LeadDetail = RouterOutputs["leads"]["get"];

const TOAST_ID = "tablero-assign-lead";

// "Pasar a un asesor" / "Reasignar asesor": assigns the conversation to a chosen
// employee and turns the AI off (leads.assignEmployee handles both branches).
const AsesorControl = ({
  merchantId,
  lead,
  hasConversation,
}: {
  merchantId: string;
  lead: LeadDetail;
  hasConversation: boolean;
}) => {
  const isMobile = useIsMobile();
  const { leadReassignmentEnabled, leadReassignmentAllowedEmails } = useContext(MerchantContext);
  const { email: callerEmail } = useContext(UserContext);
  const [open, setOpen] = useState(false);
  const utils = trpc.useUtils();

  const isAssigned = Boolean(lead.employeeId);

  const { data: employees } = trpc.employees.list.useQuery(
    { merchantId },
    { enabled: Boolean(merchantId) && isAssigned }
  );
  const assignedEmployeeName = isAssigned
    ? employees?.find((employee) => employee.id === lead.employeeId)?.name
    : undefined;

  const assignMutation = trpc.leads.assignEmployee.useMutation({
    onMutate: () => {
      toast.loading("Asignando…", { id: TOAST_ID });
    },
    onSuccess: async (result) => {
      toast.success(`Lead asignado a ${result.employeeName}`, { id: TOAST_ID });
      setOpen(false);
      posthog.capture("tablero lead assigned", {
        merchant_id: merchantId,
        lead_id: lead.id,
        employee_id: result.employeeId,
        was_assigned: isAssigned,
      });
      await Promise.all([
        utils.leads.get.invalidate({ merchantId, id: lead.id }),
        utils.conversations.get.invalidate({ merchantId, leadId: lead.id }),
        utils.conversations.listConversationTimeline.invalidate({ merchantId, leadId: lead.id }),
        // The handover rewrites employeeId/employeeName on every appointment of the
        // lead, so the perfil's Citas would keep naming the previous asesor.
        utils.appointments.list.invalidate({ merchantId, leadId: lead.id }),
        utils.leads.listActiveByPosition.invalidate(),
        utils.leads.searchActiveNonRealtime.invalidate(),
        utils.inbox.list.invalidate(),
      ]);
    },
    onError: (error) => {
      toast.error("No se pudo asignar el lead", {
        id: TOAST_ID,
        description: error.message,
      });
    },
  });

  // Every role may hand a lead over: a data-scoped employee only ever sees the
  // leads assigned to them, so the only lead they can reassign is their own —
  // handing it to a colleague who takes over. The merchant keeps the say through
  // leadReassignmentEnabled / leadReassignmentAllowedEmails, which the backend
  // enforces too (functions/merchantPortal/employees.ts `canReassignLead`).
  const canReassign = canReassignLead({
    callerEmail,
    leadReassignmentEnabled,
    leadReassignmentAllowedEmails,
  });

  if (!canReassign || !hasConversation) {
    // Still show who attends, even when the caller can't hand the lead over.
    if (!assignedEmployeeName) return null;
    return (
      <span className="flex items-center gap-1.5 text-xs text-neutral-600">
        <LeadAvatar size="sm" name={assignedEmployeeName} />
        <span className="max-w-[8rem] truncate">{assignedEmployeeName}</span>
      </span>
    );
  }

  const handleSelect = (employeeId: string) => {
    if (assignMutation.isLoading) return;
    assignMutation.mutate({ merchantId, leadId: lead.id, employeeId });
  };

  const trigger = (
    <Button variant="outline" size="sm" onClick={isMobile ? () => setOpen(true) : undefined}>
      <UserRoundPlus className="mr-1.5 size-4" />
      {isAssigned ? "Reasignar asesor" : "Pasar a un asesor"}
    </Button>
  );

  const picker = (
    <EmployeePicker
      merchantId={merchantId}
      excludeEmployeeId={lead.employeeId}
      onSelect={handleSelect}
      isPending={assignMutation.isLoading}
      pendingEmployeeId={assignMutation.variables?.employeeId}
      onEscape={() => setOpen(false)}
    />
  );

  return (
    <div className="flex min-w-0 items-center gap-2">
      {assignedEmployeeName && (
        <span className="flex items-center gap-1.5 text-xs text-neutral-600">
          <LeadAvatar size="sm" name={assignedEmployeeName} />
          <span className="max-w-[8rem] truncate">{assignedEmployeeName}</span>
        </span>
      )}
      {isMobile ? (
        <>
          {trigger}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent side="bottom" className="h-[70vh] p-4">
              <SheetHeader>
                <SheetTitle className="text-left text-base">
                  {isAssigned ? "Reasignar asesor" : "Pasar a un asesor"}
                </SheetTitle>
              </SheetHeader>
              <div className="mt-3">{picker}</div>
            </SheetContent>
          </Sheet>
        </>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          <PopoverContent align="end" className="w-[280px] p-3">
            {picker}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export default AsesorControl;
